// src/pages/portal/PortalSubmitDeal.js — "Submit a Deal" (items 9 and 10,
// Matt's C1): a partner submits a potential new RGA client, Financial or
// Strategic Advisory (migrations/073). RGA reviews it in the CRM; the
// partner sees a plain status ("With RGA", "Taken on", "Reviewed").
import { useState } from 'react'
import PortalSearch from '../../components/portal/PortalSearch'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../../lib/api'
import { workTypeLabel } from '../../lib/workTypes'
import { PORTAL_PAGE_TITLE_STYLE, PORTAL_TITLE_ALIGN } from '../../lib/portalPageTitleStyle'

const WORK_TYPES = {
  'Financial Advisory': ['Direct Capital Raising & Placement', 'M&A'],
  'Strategic Advisory': ['General Advice', 'Business Development / Client Acquisition'],
}
const DEAL_KINDS = ['Direct deal', 'Fund']
const STATUS_BADGE = { 'With RGA': 'badge-yellow', 'Taken on': 'badge-green', 'Reviewed': 'badge-gray' }

const BLANK = {
  company_name: '', work_type: 'Financial Advisory', work_subtype: '', deal_kind: '',
  website: '', location: '', description: '', the_need: '', timing: '',
  contact_name: '', contact_title: '', contact_email: '', contact_phone: '',
  relationship: '', partner_notes: '',
}

function F({ label, hint, children, wide }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5, gridColumn: wide ? '1 / -1' : undefined }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{label}</span>
      {children}
      {hint && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{hint}</span>}
    </label>
  )
}

function YourDeals() {
  const { data } = useQuery({
    queryKey: ['portalMyDeals'],
    queryFn: () => api.get('/api/portal/my-deals').then(r => r.data.data ?? []),
  })
  if (!data?.length) return null
  return (
    <div style={{ marginTop: 32 }}>
      <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Your deal submissions</h2>
      <div className="table-wrapper">
        <table>
          <thead><tr><th>Company or fund</th><th>Work type</th><th>Submitted</th><th>Status</th></tr></thead>
          <tbody>
            {data.map(d => (
              <tr key={d.deal_submission_id}>
                <td style={{ fontWeight: 600 }}>{d.company_name}</td>
                <td>{[workTypeLabel(d.work_type, true), d.work_subtype].filter(Boolean).join(' / ')}</td>
                <td>{new Date(d.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                <td><span className={`badge ${STATUS_BADGE[d.status_label] ?? 'badge-gray'}`}>{d.status_label}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function PortalSubmitDeal() {
  const qc = useQueryClient()
  const [form, setForm] = useState(BLANK)
  const [done, setDone] = useState(null)
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  const submit = useMutation({
    mutationFn: () => api.post('/api/portal/deals', form).then(r => r.data.data),
    onSuccess: d => {
      setDone(d.company_name)
      setForm(BLANK)
      qc.invalidateQueries({ queryKey: ['portalMyDeals'] })
      qc.invalidateQueries({ queryKey: ['portalDashboard'] })
    },
  })

  return (
    <div className="page" style={{ maxWidth: 1000 }}>
      <div className="page-header">
        <div className="page-header-text" style={{ flex: 1 }}>
          <h1 style={PORTAL_PAGE_TITLE_STYLE}>Submit a Deal</h1>
          <p style={{ textAlign: PORTAL_TITLE_ALIGN }}>
            Introduce a potential new RGA client. RGA reviews every submission and will follow up with you.
          </p>
        </div>
        <PortalSearch />
      </div>

      {done && (
        <div className="card" style={{ marginBottom: 16, fontSize: 13, borderColor: 'var(--success, #10b981)' }}>
          Thank you. {done} is with RGA for review.
        </div>
      )}

      <form className="card" onSubmit={e => { e.preventDefault(); setDone(null); submit.mutate() }}
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: 16 }}>
        <F label="Company or fund *" wide>
          <input className="input" value={form.company_name} onChange={set('company_name')} required />
        </F>
        <F label="Type of work *">
          <select className="select" value={form.work_type}
            onChange={e => setForm(f => ({ ...f, work_type: e.target.value, work_subtype: '' }))}>
            {Object.keys(WORK_TYPES).map(t => <option key={t} value={t}>{workTypeLabel(t, true)}</option>)}
          </select>
        </F>
        <F label="Sub-type">
          <select className="select" value={form.work_subtype} onChange={set('work_subtype')}>
            <option value="">Not sure</option>
            {WORK_TYPES[form.work_type].map(t => <option key={t}>{t}</option>)}
          </select>
        </F>
        <F label="Direct deal or fund">
          <select className="select" value={form.deal_kind} onChange={set('deal_kind')}>
            <option value="">Not sure</option>
            {DEAL_KINDS.map(t => <option key={t}>{t}</option>)}
          </select>
        </F>
        <F label="Website"><input className="input" value={form.website} onChange={set('website')} /></F>
        <F label="Location"><input className="input" value={form.location} onChange={set('location')} /></F>
        <F label="Timing"><input className="input" value={form.timing} onChange={set('timing')} placeholder="e.g. raising in Q1" /></F>
        <F label="What they do" wide>
          <textarea className="textarea" rows={3} value={form.description} onChange={set('description')} />
        </F>
        <F label="What they need from RGA" wide hint="e.g. the size of the raise, or the advice they are looking for">
          <textarea className="textarea" rows={3} value={form.the_need} onChange={set('the_need')} />
        </F>
        <F label="Your contact there"><input className="input" value={form.contact_name} onChange={set('contact_name')} /></F>
        <F label="Their title"><input className="input" value={form.contact_title} onChange={set('contact_title')} /></F>
        <F label="Their email"><input className="input" type="email" value={form.contact_email} onChange={set('contact_email')} /></F>
        <F label="Their phone"><input className="input" value={form.contact_phone} onChange={set('contact_phone')} /></F>
        <F label="How you know them" wide>
          <textarea className="textarea" rows={2} value={form.relationship} onChange={set('relationship')} />
        </F>
        <F label="Anything else RGA should know" wide>
          <textarea className="textarea" rows={3} value={form.partner_notes} onChange={set('partner_notes')} />
        </F>
        <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
          {submit.isError && (
            <span style={{ fontSize: 13, color: 'var(--danger-text)' }}>
              {submit.error.response?.data?.error || submit.error.message}
            </span>
          )}
          <button type="submit" className="btn btn-primary" disabled={submit.isPending || !form.company_name.trim()}>
            {submit.isPending ? 'Submitting…' : 'Submit to RGA'}
          </button>
        </div>
      </form>

      <YourDeals />
    </div>
  )
}
